import { useState, useEffect } from 'react';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, ChevronLeft, ChevronRight, Edit, Trash2,
  Bell, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const initialFormData = {
  nombre: '',
  dias: '',
  activo: 1,
};

const TiemposAlertaPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('config', 'tiempos_alerta');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'dias', dir: 'asc' });
  
  // Filters
  const [searchFilter, setSearchFilter] = useState('');
  
  // Modals
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
      });
      
      if (searchFilter) params.append('search', searchFilter);

      const response = await api.get(`/config/tiempos-alerta?${params}`);
      setItems(response.data.items);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar tiempos de alerta');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchItems();
  }, [pagination.page, sortConfig]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchItems();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter]);

  // CRUD handlers
  const handleCreate = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, item: null });
  };

  const handleEdit = (item) => {
    setFormData({
      nombre: item.nombre || '',
      dias: item.dias ? String(item.dias) : '',
      activo: item.activo ? 1 : 0,
    });
    setEditModal({ open: true, item });
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.dias || parseInt(formData.dias) <= 0) {
      toast.error('Los dias deben ser mayor a 0');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre.trim(),
        dias: parseInt(formData.dias),
        activo: formData.activo,
      };
      
      if (editModal.item) {
        await api.put(`/config/tiempos-alerta/${editModal.item.id}`, payload);
        toast.success('Tiempo de alerta actualizado');
      } else {
        await api.post('/config/tiempos-alerta', payload);
        toast.success('Tiempo de alerta creado');
      }
      setEditModal({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/config/tiempos-alerta/${deleteModal.item.id}`);
      toast.success('Tiempo de alerta eliminado');
      setDeleteModal({ open: false, item: null });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Tiempos de Alerta</h1>
            <p className="page-subtitle">Catalogo de tiempos de alerta para licencias (30 dias, 60 dias, etc.)</p>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-tiempo-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Tiempo
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-4" data-testid="tiempos-alerta-stats">
          <div className="stats-card" data-testid="stats-total">
            <div className="mb-2">
              <Bell className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{pagination.total}</div>
            <div className="stats-card-label">Total Tiempos</div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('nombre')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="nombre" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none text-center"
                          onClick={() => handleSort('dias')}
                        >
                          <div className="flex items-center justify-center gap-1">
                            DIAS
                            <SortIcon field="dias" />
                          </div>
                        </th>
                        <th className="text-center">ACTIVO</th>
                        <th className="w-20 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4}>
                            <div className="empty-state">
                              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron tiempos de alerta</p>
                            </div>
                          </td>
                        </tr>
                      ) : items.map((item) => (
                        <tr key={item.id} data-testid={`tiempo-row-${item.id}`}>
                          <td className="font-medium text-primary">{item.nombre}</td>
                          <td className="text-center">
                            <span className="badge badge-info">{item.dias} dias</span>
                          </td>
                          <td className="text-center">
                            {item.activo ? (
                              <span className="badge badge-success">Activo</span>
                            ) : (
                              <span className="badge badge-outline">Inactivo</span>
                            )}
                          </td>
                          <td className="w-20">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => canUpdate && handleEdit(item)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-tiempo-${item.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, item })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-tiempo-${item.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="text-sm text-muted-foreground">
                    {pagination.total > 0 ? `${(pagination.page - 1) * pagination.page_size + 1} - ${Math.min(pagination.page * pagination.page_size, pagination.total)} de ${pagination.total}` : '0 registros'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{pagination.page} / {pagination.pages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                      disabled={pagination.page >= pagination.pages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit/Create Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, item: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                {editModal.item ? 'Editar Tiempo de Alerta' : 'Nuevo Tiempo de Alerta'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: 30 dias antes, 60 dias antes"
                  data-testid="input-nombre"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Dias *</Label>
                <Input
                  type="number"
                  value={formData.dias}
                  onChange={(e) => setFormData(prev => ({ ...prev, dias: e.target.value }))}
                  placeholder="30"
                  data-testid="input-dias"
                />
                <p className="text-xs text-muted-foreground">Cantidad de dias antes del vencimiento para generar alerta</p>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Activo</Label>
                <div className="flex items-center gap-2 h-9">
                  <Switch
                    checked={formData.activo === 1}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, activo: checked ? 1 : 0 }))}
                    data-testid="switch-activo"
                  />
                  <span className="text-sm text-muted-foreground">
                    {formData.activo === 1 ? 'Si' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, item: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Tiempo de Alerta
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar el tiempo de alerta <strong>{deleteModal.item?.nombre}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, item: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TiemposAlertaPage;
